import axios from 'axios';
import i18n from '../i18n/i18n'; // Importar i18n para acceder al idioma

// Memory cache for API responses
const apiCache = {
  data: new Map(),
  
  /**
   * Get cached response if available and not expired
   * @param {string} key - Cache key
   * @param {number} maxAge - Maximum age in milliseconds
   * @returns {any|null} - Cached data or null
   */
  get(key, maxAge = 60000) { // Default 1 minute cache
    const cached = this.data.get(key);
    
    if (!cached) return null;
    
    const now = Date.now();
    if (now - cached.timestamp > maxAge) {
      // Cache expired
      this.data.delete(key);
      return null;
    }
    
    return cached.data;
  },
  
  /**
   * Store data in cache
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   */
  set(key, data) {
    this.data.set(key, {
      data,
      timestamp: Date.now()
    });
  },
  
  /**
   * Clear entire cache or specific key
   * @param {string|null} key - Specific key to clear (null for entire cache)
   */
  clear(key = null) {
    if (key === null) {
      this.data.clear();
    } else {
      this.data.delete(key);
    }
  }
};

// Utility function for debouncing API calls
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Cambiar la URL base según el entorno
const API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://summalytool.hoople.app/api'  // URL en producción
  : 'http://localhost:8000/api';           // URL en desarrollo

// FIXME: Simular API token para pruebas - en producción obtener de una fuente segura
const API_TOKEN = process.env.REACT_APP_API_TOKEN || '';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest', // Ayuda a identificar solicitudes AJAX
  },
  // Configurado según lo que necesitemos
  withCredentials: false // Cambiado a false para evitar problemas de CORS si no usamos autenticación
});

// Add token to requests when available
if (API_TOKEN) {
  apiClient.defaults.headers.common['X-API-Token'] = API_TOKEN;
}

// Add response interceptor to handle common errors
apiClient.interceptors.response.use(
  response => response,
  error => {
    // Handle rate limiting
    if (error.response && error.response.status === 429) {
      // You could implement a retry mechanism with exponential backoff here
    }
    
    // Parse error messages into a user-friendly format
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      let errorMessage = 'An unexpected error occurred';
      
      // If the server returned a message, use it
      if (errorData.message) {
        errorMessage = errorData.message;
      }
      
      // If there are validation errors, format them
      if (errorData.errors) {
        const validationErrors = Object.values(errorData.errors)
          .flat()
          .join('. ');
        
        if (validationErrors) {
          errorMessage = validationErrors;
        }
      }
      
      // Add a friendly message to the error
      error.friendlyMessage = errorMessage;
    }
    
    return Promise.reject(error);
  }
);

export const summarizeUrl = async (url) => {
  // Obtener el idioma actual
  const currentLanguage = i18n.language.split('-')[0]; // Extraer código principal (en-US → en)
  
  try {
    // Enviar URL y el idioma al backend
    const response = await apiClient.post('/summarize', { 
      url: url,
      language: currentLanguage // Añadir el idioma a la petición
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      // Respuesta del servidor con código de error
      
      // Handle specific error types
      if (error.response.status === 422) {
        // Validation error
        throw new Error(error.friendlyMessage || 'The URL provided is invalid or cannot be processed.');
      } else if (error.response.status === 429) {
        // Rate limit error
        throw new Error('Too many requests. Please try again in a few minutes.');
      } else if (error.response.status >= 500) {
        // Server error
        throw new Error('The server encountered an error. Please try again later.');
      }
    } else if (error.request) {
      // El servidor no respondió
      throw new Error('Could not connect to server. Please check your internet connection.');
    } else {
      // Error en la configuración de la petición
    }
    
    // Generic error fallback
    throw new Error(error.friendlyMessage || 'An error occurred while processing your request.');
  }
};

export const getLatestNews = async () => {
  // Create cache key
  const cacheKey = 'latest_news';
  
  // Check cache first (valid for 5 minutes)
  const cachedData = apiCache.get(cacheKey, 5 * 60 * 1000);
  if (cachedData) {
    return cachedData;
  }
  
  try {
    const response = await apiClient.get('/news/latest');
    
    // Cache the successful response
    apiCache.set(cacheKey, response.data);
    
    return response.data;
  } catch (error) {
    throw new Error(error.friendlyMessage || 'Could not load latest news.');
  }
};

export const getAllNews = async (page = 1, limit = 10) => {
  // Create cache key including pagination parameters
  const cacheKey = `all_news_page${page}_limit${limit}`;
  
  // Check cache first (valid for 3 minutes)
  const cachedData = apiCache.get(cacheKey, 3 * 60 * 1000);
  if (cachedData) {
    return cachedData;
  }
  
  try {
    const response = await apiClient.get('/news', {
      params: { page, limit },
    });
    
    // Cache the successful response
    apiCache.set(cacheKey, response.data);
    
    return response.data;
  } catch (error) {
    throw new Error(error.friendlyMessage || 'Could not load news list.');
  }
}; 